// App Migration Direct Bottom Sheet Component
// Variant B: Opens the bottom sheet directly on page load without TopBanner or FloatingBanner
// Used when experiment variant is 'app-migration-b'

import React, { memo, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Helpers
import { getEligibleVariantName } from 'shared/components/ExperimentV2/helpers';
import { log } from 'shared/helpers/newRelic';
import { TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';

// Hooks
import {
  useAppInstallDetection,
  useAppsFlyerSmartScript,
} from 'shared/components/AppMigration/hooks';

// Helpers
import {
  isBannerDismissed,
  dismissBanner,
  getPageType,
  navigateToURL,
} from 'shared/components/AppMigration/helpers';

// Constants
import {
  CTA_TEXT,
  APP_MIGRATION_EXP,
  APP_MIGRATION_EXP_VARIANT_B,
  UTM_CAMPAIGN,
  UTM_CONTENT,
} from 'shared/components/AppMigration/constants';

// Components
import AppMigrationBottomSheet, {
  preloadBottomSheetImages,
} from 'shared/components/AppMigration/AppMigrationBottomSheet';

// Types
import { IAppMigrationBannerProps, BannerCTAText } from 'shared/components/AppMigration/types';

/**
 * AppMigrationDirectBottomSheet - Variant B of the app migration experiment
 *
 * Instead of showing a TopBanner + FloatingBanner flow, this variant
 * directly opens the BottomSheet on page load (once per session).
 *
 * Behavior:
 * - Opens bottom sheet automatically on first page load
 * - If user closes, it won't reopen until next session
 * - Respects the same dismiss logic as other banners (24-hour cooldown)
 * - No top banner or floating banner shown
 *
 * @param pageType - The page type for analytics tracking
 * @param onCTAClick - Callback when CTA is clicked
 */
function AppMigrationDirectBottomSheet({ onCTAClick }: IAppMigrationBannerProps) {
  const { pathname, search } = useLocation();
  const pageType = getPageType(pathname);
  const isSearchOpen = search.includes('showsearch');

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const hasOpenedRef = useRef(false);

  // AB Experiment for feature flag
  const { isEligible = false, variant = '' } = getEligibleVariantName(APP_MIGRATION_EXP);
  const isFeatureEnabled = isEligible && variant === APP_MIGRATION_EXP_VARIANT_B;

  // App installation detection
  const { isLoading: isDetecting, isMobile, platform, isAppInstalled } = useAppInstallDetection();

  // AppsFlyer Smart Script - only load when feature is enabled and on mobile
  const autoLoad = isFeatureEnabled && isMobile;
  const { generateURL } = useAppsFlyerSmartScript({
    autoLoad: autoLoad,
    additionalParams: {
      utmCampaign: UTM_CAMPAIGN,
      utmMedium: pageType,
      utmContent: UTM_CONTENT.DIRECT_BOTTOM_SHEET,
      trackingCode: variant,
    },
  });

  // Auto-open bottom sheet on mount (once per session)
  useEffect(() => {
    // Wait for detection and experiment to resolve
    if (isDetecting || !isFeatureEnabled) return;

    // Only trigger once
    if (hasOpenedRef.current) return;

    // Only show on mobile web
    if (!isMobile) return;

    // Check if banner was dismissed recently (24hr cooldown)
    if (isBannerDismissed()) return;

    hasOpenedRef.current = true;

    // Preload images and open the bottom sheet
    preloadBottomSheetImages();

    // Small delay to let the page render first
    const timer = setTimeout(() => {
      setIsBottomSheetOpen(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isDetecting, isMobile, isFeatureEnabled]);

  /**
   * Handle bottom sheet close
   */
  const handleBottomSheetClose = useCallback(() => {
    setIsBottomSheetOpen(false);
    // Dismiss so it doesn't reopen for 24 hours
    dismissBanner();
  }, []);

  /**
   * Handle download button click
   */
  const handleDownload = useCallback(async () => {
    try {
      const url = await generateURL({
        page_type: pageType,
        platform: platform,
        deep_link_value: window.location.href,
        af_dp: window.location.href,
        af_web_dp: window.location.href,
      });

      if (onCTAClick) {
        onCTAClick(url);
      }

      // Navigate via anchor click so Android App Links can intercept
      navigateToURL(url);
    } catch (error) {
      log({
        errTitle: '[AppMigration] Error generating OneLink URL',
        error,
        tags: { [TAG_IDENTIFIER.MODULE]: 'AppMigrationDirectBottomSheet' },
      });
      if (onCTAClick) {
        onCTAClick('');
      }
    }
  }, [generateURL, pageType, platform, onCTAClick]);

  /**
   * Determine CTA text based on platform and app install status
   * - iOS: Always "Download Now"
   * - Android with app installed: "Use App"
   * - Android without app: "Download Now"
   */
  const getCTAText = useCallback((): BannerCTAText => {
    if (platform === 'ios') {
      return CTA_TEXT.DOWNLOAD_NOW;
    }
    if (platform === 'android' && isAppInstalled) {
      return CTA_TEXT.USE_APP;
    }
    return CTA_TEXT.DOWNLOAD_NOW;
  }, [platform, isAppInstalled]);

  // Don't render during SSR
  if (typeof window === 'undefined') return null;

  // Don't render when search is open
  if (isSearchOpen) return null;

  // Don't render if feature not enabled
  if (!isFeatureEnabled) return null;

  return (
    <AppMigrationBottomSheet
      isOpen={isBottomSheetOpen}
      onClose={handleBottomSheetClose}
      onDownload={handleDownload}
      ctaText={getCTAText()}
    />
  );
}

export default memo(AppMigrationDirectBottomSheet);
